// P:\0PJ\synforge\frontend\components\layout\RootLayoutClient.tsx
// v5 2026.09.19
//   v5: 데스크톱에서 사용자가 사이드바를 접어놨는데 새로고침/다른 페이지
//       이동 시 다시 펼쳐지던 문제 수정 — collapsed 상태가 리액트 메모리
//       에만 있고 어디에도 저장되지 않아 마운트될 때마다 기본값(펼침)으로
//       리셋됐다. localStorage("sidebarCollapsed")에 데스크톱 한정으로
//       저장/복원한다(이 기기·이 브라우저 전용, 모바일/태블릿은 매번 접힘
//       레일로 시작하는 기존 규칙 유지)(Hugh 확인, 2026.09.19).
"use client"

import { usePathname } from "next/navigation"
import { useEffect, useState } from "react"
import Sidebar from "@/components/layout/Sidebar"

const NO_SIDEBAR_PATHS = ["/login", "/register", "/", "/terms"]
const COLLAPSE_STORAGE_KEY = "sidebarCollapsed"

type Breakpoint = "phone" | "phoneWide" | "tablet" | "desktop"

// 폭 구간 — 기기 종류가 아니라 현재 창 폭만 본다. 폴더블 펼침/접힘, 태블릿
//   회전 모두 resize 이벤트로 잡히므로 별도 기기 분기 없이 자동 대응된다.
function getBreakpoint(width: number): Breakpoint {
  if (width < 640) return "phone"
  if (width < 768) return "phoneWide"
  if (width < 1024) return "tablet"
  return "desktop"
}

// 펼쳤을 때 사이드바 폭 — 화면 종류별로 다르게.
function getExpandedWidthClass(breakpoint: Breakpoint): string {
  switch (breakpoint) {
    case "phone": return "w-[86vw] max-w-[300px]"
    case "phoneWide": return "w-[280px]"
    case "tablet": return "w-[300px]"
    default: return "w-[200px]"
  }
}

// 데스크톱 한정 — localStorage 에 저장된 사용자 선호를 읽는다. 없으면(최초
//   방문 등) null 을 반환해 호출부가 기본값(펼침)을 쓰게 한다.
function readStoredCollapsed(): boolean | null {
  try {
    const v = localStorage.getItem(COLLAPSE_STORAGE_KEY)
    return v === null ? null : v === "1"
  } catch {
    return null
  }
}

function writeStoredCollapsed(value: boolean) {
  try {
    localStorage.setItem(COLLAPSE_STORAGE_KEY, value ? "1" : "0")
  } catch {
    // 저장 실패(프라이빗 모드 등)는 무시 — 이 세션만 기억 못 할 뿐 기능에는 지장 없다.
  }
}

const RAIL_WIDTH = "w-14" // 접힘 상태 — 항상 아이콘 한 줄(56px)은 보인다.

export default function RootLayoutClient({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const [breakpoint, setBreakpoint] = useState<Breakpoint>("desktop")
  const [collapsed, setCollapsed] = useState(false)

  // 사용자가 직접 접기/펼치기를 바꿀 때 쓰는 헬퍼 — 데스크톱일 때만
  //   localStorage 에 남겨 다음 로드 때 복원한다(모바일/태블릿은 매번
  //   레일로 시작하는 게 자연스러워 저장하지 않는다).
  const updateCollapsed = (next: boolean) => {
    setCollapsed(next)
    if (breakpoint === "desktop") writeStoredCollapsed(next)
  }

  // 최초 마운트 — 실제 폭을 계산해 breakpoint 를 정하고, 데스크톱이면
  //   저장된 접힘 선호를 복원한다(없으면 기본 펼침). 모바일/태블릿은
  //   항상 접힘(레일)으로 시작한다.
  useEffect(() => {
    const bp = getBreakpoint(window.innerWidth)
    setBreakpoint(bp)
    if (bp === "desktop") {
      const stored = readStoredCollapsed()
      setCollapsed(stored ?? false)
    } else {
      setCollapsed(true)
    }
  }, [])

  // 리사이즈 — desktop <-> 모바일/태블릿 경계를 넘을 때만 collapsed 기본값을
  //   다시 정한다. 같은 그룹 안에서 폭만 바뀌는 경우(회전, 폴더블 펼침)는
  //   사용자가 이미 선택한 상태를 건드리지 않는다.
  useEffect(() => {
    const onResize = () => {
      const bp = getBreakpoint(window.innerWidth)
      setBreakpoint((prev) => {
        const wasDesktop = prev === "desktop"
        const isDesktop = bp === "desktop"
        if (wasDesktop !== isDesktop) {
          if (isDesktop) {
            const stored = readStoredCollapsed()
            setCollapsed(stored ?? false)
          } else {
            setCollapsed(true)
          }
        }
        return bp
      })
    }
    window.addEventListener("resize", onResize)
    return () => window.removeEventListener("resize", onResize)
  }, [])

  const showSidebar = !NO_SIDEBAR_PATHS.includes(pathname)
  const isOverlay = breakpoint !== "desktop"
  const expandedWidthClass = getExpandedWidthClass(breakpoint)

  if (!showSidebar) {
    return <>{children}</>
  }

  return (
    <div className="flex h-[100dvh] bg-[#0a0a0f] overflow-hidden relative">

      {/* 펼쳤을 때(모바일/태블릿)만 뒤 배경 어둡게 + 탭하면 레일로 복귀 */}
      {isOverlay && !collapsed && (
        <button
          type="button"
          aria-label="사이드바 접기"
          onClick={() => updateCollapsed(true)}
          className="fixed inset-0 z-40 bg-black/60"
        />
      )}

      {/* 사이드바 컨테이너 — 접힘 상태도 폭이 56px(레일)이라 절대 0이
          되지 않는다. 데스크톱은 밀어내기(relative/shrink-0), 모바일/
          태블릿은 fixed 오버레이. */}
      <div className={`${isOverlay ? "fixed inset-y-0 left-0 z-50" : "relative shrink-0"} transition-all duration-200`}>
        <div className={`h-full transition-all duration-200 ${collapsed ? RAIL_WIDTH : expandedWidthClass}`}>
          <Sidebar collapsed={collapsed} onExpand={() => updateCollapsed(false)} />
        </div>

        {/* 펼침/접힘 토글 — 컨테이너 최소 폭이 56px 이상이라 버튼이 화면
            밖으로 밀려날 일이 없다. */}
        <button
          type="button"
          aria-label={collapsed ? "사이드바 펼치기" : "사이드바 접기"}
          onClick={() => updateCollapsed(!collapsed)}
          className="absolute top-3 -right-3 z-50 w-6 h-6 bg-[#0d0d14] hover:bg-white/[0.08] border border-white/[0.10] rounded-md flex items-center justify-center transition-all shadow-md"
        >
          <span className={`text-white/40 text-[11px] font-bold transition-transform duration-200 ${collapsed ? "rotate-180" : ""}`}>
            ‹
          </span>
        </button>
      </div>

      {/* 모바일/태블릿은 사이드바가 fixed 라 본문이 안 밀리므로, 레일
          폭만큼 왼쪽 여백을 직접 준다. */}
      <main className={`flex-1 overflow-hidden relative transition-all duration-200 ${isOverlay ? "pl-14" : ""}`}>
        {children}
      </main>

    </div>
  )
}